/**
 * Real OCR Text Extraction Service
 * Uses Tesseract.js for actual text extraction from recipe images
 */

import { createWorker } from 'tesseract.js';

/**
 * Extract text from recipe image using Tesseract.js OCR
 * @param {File} imageFile - Recipe photo
 * @returns {Promise<Object>} - Extracted recipe data
 */
export async function extractRecipeText(imageFile) {
  try {
    console.log('📖 Starting real OCR text extraction with Tesseract.js...');
    
    // Create Tesseract worker
    const worker = await createWorker('eng');
    
    // Extract text from image
    const extractedText = await extractTextFromImage(imageFile, worker);
    
    // Cleanup worker
    await worker.terminate();
    
    if (!extractedText || extractedText.trim().length < 10) {
      throw new Error('No readable text found in image');
    }
    
    console.log('📝 Extracted text:', extractedText);
    
    // Parse the extracted text into structured recipe data
    const parsedRecipe = parseRecipeText(extractedText);
    
    // Enhance with AI if needed
    const enhancedRecipe = await enhanceRecipeWithAI(parsedRecipe, extractedText);
    
    return {
      success: true,
      rawText: extractedText,
      parsedRecipe: enhancedRecipe,
      confidence: calculateConfidence(extractedText, enhancedRecipe),
      needsReview: true
    };
  } catch (error) {
    console.error('❌ OCR extraction failed:', error);
    
    // Fallback to mock data with warning
    return {
      success: false,
      rawText: 'OCR failed - using fallback data',
      parsedRecipe: getFallbackRecipe(),
      confidence: 0.1,
      needsReview: true,
      error: error.message
    };
  }
}

/**
 * Extract text from image using Tesseract.js OCR with image preprocessing
 */
async function extractTextFromImage(imageFile, worker) {
  try {
    console.log('🔍 Running Tesseract.js OCR on image...');
    
    // Configure Tesseract for better text recognition
    await worker.setParameters({
      tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789.,;:!?()[]{}"\'-/&%$@#+= ',
      tessedit_pageseg_mode: '1', // Automatic page segmentation with OSD
      preserve_interword_spaces: '1'
    });
    
    // Perform OCR on the image
    const { data: { text, confidence } } = await worker.recognize(imageFile);
    
    console.log('✅ OCR completed, extracted text length:', text.length);
    console.log('📊 OCR confidence:', confidence);
    
    if (!text || text.trim().length < 5) {
      console.log('⚠️ OCR returned insufficient text, trying with different settings...');
      
      // Try with different settings for poor quality images
      await worker.setParameters({
        tessedit_pageseg_mode: '6', // Uniform block of text
        tessedit_ocr_engine_mode: '1' // Neural nets LSTM engine only
      });
      
      const { data: { text: retryText } } = await worker.recognize(imageFile);
      console.log('🔄 Retry OCR text length:', retryText.length);
      
      if (retryText && retryText.trim().length > text.trim().length) {
        return retryText;
      }
    }
    
    if (!text || text.trim().length < 5) {
      throw new Error('OCR returned insufficient text');
    }
    
    return text;
  } catch (error) {
    console.error('Error in Tesseract OCR:', error);
    throw error;
  }
}

/**
 * Parse raw OCR text into structured recipe data
 */
function parseRecipeText(rawText) {
  console.log('📝 Parsing OCR text:', rawText);
  
  const lines = rawText.split('\n').map(line => line.trim()).filter(line => line.length > 0);
  
  let recipeName = '';
  let ingredients = [];
  let instructions = [];
  let prepTime = '';
  let cookTime = '';
  let servings = '';
  
  let currentSection = '';
  
  // If OCR text is very short or garbled, try to extract what we can
  if (rawText.length < 20) {
    console.log('⚠️ OCR text is very short, attempting basic extraction...');
    
    // Try to find any recognizable words
    const words = rawText.split(/\s+/).filter(word => word.length > 2);
    if (words.length > 0) {
      recipeName = words.join(' ');
    }
    
    // If we can't parse much, return what we have without fallback recipes
    return {
      name: recipeName || 'Recipe from Photo',
      ingredients: ['Please manually enter ingredients'],
      instructions: ['Please manually enter instructions'],
      prepTime: '',
      cookTime: '',
      servings: '4',
      difficulty: 'Easy',
      ocrQuality: 'poor'
    };
  }
  
  // Smart section detection with better boundary recognition
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lowerLine = line.toLowerCase();
    
    // Detect section headers more precisely
    if (lowerLine.includes('recipe') || lowerLine.includes('name') || lowerLine.includes('title')) {
      if (lowerLine.includes(':')) {
        recipeName = line.split(':')[1]?.trim() || '';
      } else if (!recipeName && line.length > 3) {
        recipeName = line;
      }
      currentSection = 'name';
    } else if (lowerLine.includes('ingredient')) {
      currentSection = 'ingredients';
      // Clear any previous ingredients that might be instructions
      ingredients = [];
    } else if (lowerLine.includes('instruction') || lowerLine.includes('direction') || lowerLine.includes('step') || lowerLine.includes('method')) {
      currentSection = 'instructions';
      // Clear any previous instructions that might be ingredients
      instructions = [];
    } else if (lowerLine.includes('prep') && lowerLine.includes('time')) {
      prepTime = line.split(':')[1]?.trim() || '';
    } else if (lowerLine.includes('cook') && lowerLine.includes('time')) {
      cookTime = line.split(':')[1]?.trim() || '';
    } else if (lowerLine.includes('serving') || lowerLine.includes('yield')) {
      servings = line.split(':')[1]?.trim() || '';
    } else {
      // Add to current section with smarter parsing
      if (currentSection === 'ingredients') {
        // Ingredients should have measurements or quantities
        if (line.startsWith('-') || line.startsWith('•') || 
            /^\d+/.test(line) || // starts with number
            line.includes('cup') || line.includes('tbsp') || line.includes('tsp') || 
            line.includes('oz') || line.includes('lb') || line.includes('g') || line.includes('ml') ||
            line.includes('can') || line.includes('package') || line.includes('bunch') ||
            line.includes('pinch') || line.includes('dash') || line.includes('to taste')) {
          ingredients.push(line.replace(/^[-•]\s*/, '').trim());
        }
      } else if (currentSection === 'instructions') {
        // Instructions should be longer and contain action words
        if (/^\d+\./.test(line) || line.includes('step') || 
            line.length > 15 || 
            /(mix|heat|bake|cook|stir|add|combine|preheat|chop|cut|slice|dice|pour|sprinkle|season|taste)/i.test(line)) {
          instructions.push(line);
        }
      } else if (currentSection === 'name' && !recipeName && line.length > 3) {
        recipeName = line;
      } else if (!currentSection && line.length > 5) {
        // Try to guess what this line is based on content
        if (/^\d+/.test(line) && (line.includes('cup') || line.includes('tbsp') || line.includes('tsp') || line.includes('oz') || line.includes('lb'))) {
          // Likely an ingredient
          if (!currentSection) currentSection = 'ingredients';
          ingredients.push(line);
        } else if (/^\d+\./.test(line) || /(mix|heat|bake|cook|stir|add|combine|preheat)/i.test(line)) {
          // Likely an instruction
          if (!currentSection) currentSection = 'instructions';
          instructions.push(line);
        } else if (!recipeName && line.length > 3 && line.length < 50) {
          // Likely a recipe name
          recipeName = line;
        }
      }
    }
  }
  
  // Clean up and validate the extracted data
  const result = {
    name: recipeName || 'Recipe from Photo',
    ingredients: ingredients.filter(ing => ing.length > 2),
    instructions: instructions.filter(inst => inst.length > 5),
    prepTime,
    cookTime,
    servings,
    difficulty: calculateDifficulty(instructions, ingredients),
    ocrQuality: rawText.length > 50 ? 'good' : 'poor'
  };
  
  console.log('📋 Parsed recipe:', result);
  return result;
}

/**
 * Enhance recipe with AI-generated content
 */
async function enhanceRecipeWithAI(basicRecipe, originalText) {
  try {
    console.log('🤖 Enhancing recipe with AI...');
    
    // Simulate AI enhancement
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const enhanced = {
      ...basicRecipe,
      // Add missing instructions if recipe only has ingredients
      instructions: basicRecipe.instructions.length > 0 ? basicRecipe.instructions : generateInstructions(basicRecipe.ingredients),
      // Add cooking tips
      tips: generateCookingTips(basicRecipe.name, basicRecipe.ingredients),
      // Add variations
      variations: generateVariations(basicRecipe.name),
      // Add nutrition info
      nutrition: generateNutritionInfo(basicRecipe.ingredients),
      // Add serving size info
      servingInfo: {
        baseServings: parseInt(basicRecipe.servings) || 4,
        adjustable: true
      }
    };
    
    console.log('✅ Recipe enhanced with AI');
    return enhanced;
  } catch (error) {
    console.error('Error enhancing recipe:', error);
    return basicRecipe;
  }
}

/**
 * Generate instructions for ingredient-only recipes
 */
function generateInstructions(ingredients) {
  // Don't generate generic instructions - let the user enter them manually
  return ["Please manually enter the cooking instructions from your recipe"];
}

/**
 * Generate cooking tips based on recipe
 */
function generateCookingTips(recipeName, ingredients) {
  // Only provide generic tips, no recipe-specific content
  return [
    "For best results, use room temperature ingredients",
    "Don't overmix the dough to avoid tough results", 
    "Check doneness by inserting a toothpick - it should come out clean",
    "Read the recipe thoroughly before starting"
  ];
}

/**
 * Generate recipe variations
 */
function generateVariations(recipeName) {
  const variations = [
    "Add 1/2 cup chopped nuts for extra crunch",
    "Try different extract flavors (vanilla, almond, etc.)",
    "Substitute some flour with cocoa powder for chocolate version"
  ];
  
  if (recipeName.toLowerCase().includes('mint')) {
    variations.push("Use white chocolate chips instead of mint chips");
    variations.push("Add green food coloring for festive look");
  }
  
  return variations;
}

/**
 * Generate nutrition information
 */
function generateNutritionInfo(ingredients) {
  const hasChocolate = ingredients.some(ing => ing.toLowerCase().includes('chocolate'));
  const hasNuts = ingredients.some(ing => ing.toLowerCase().includes('nut'));
  
  return {
    calories: hasChocolate ? '180-220' : '150-180',
    protein: hasNuts ? '3-4g' : '2-3g',
    carbs: '20-25g',
    fat: '8-12g',
    fiber: hasNuts ? '1-2g' : '0.5-1g'
  };
}

/**
 * Calculate recipe difficulty
 */
function calculateDifficulty(instructions, ingredients) {
  if (instructions.length > 8 || ingredients.length > 10) return 'Hard';
  if (instructions.length > 5 || ingredients.length > 6) return 'Medium';
  return 'Easy';
}

/**
 * Calculate confidence score
 */
function calculateConfidence(rawText, parsedRecipe) {
  let score = 0;
  
  if (parsedRecipe.name && parsedRecipe.name.length > 2) score += 0.3;
  if (parsedRecipe.ingredients.length > 3) score += 0.3;
  if (parsedRecipe.instructions.length > 2) score += 0.3;
  if (parsedRecipe.prepTime || parsedRecipe.cookTime) score += 0.1;
  
  return Math.min(score, 1.0);
}

/**
 * Get fallback recipe when OCR fails
 */
function getFallbackRecipe() {
  return {
    name: 'Recipe from Photo',
    ingredients: ['Please manually enter ingredients'],
    instructions: ['Please manually enter instructions'],
    prepTime: '',
    cookTime: '',
    servings: '4',
    difficulty: 'Easy'
  };
}
